import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  FileText, Search, ArrowRightLeft, Clock, Plus, LayoutGrid, Loader2, RefreshCw, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Box, Filter, ArrowUpDown 
} from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermissions } from './usePermissions';
import { exportToExcel } from './exportUtils'; 

const InventoryUsageList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreate } = usePermissions('inventoryusage'); // 🛡️ الصلاحيات

  // --- Session Data ---
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';

  // --- State ---
  const [loading, setLoading] = useState(false);
  const [usageRecords, setUsageRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [showAllSites, setShowAllSites] = useState(searchParams.get('all_sites') === 'true');

  // --- URL Params (Persistence) ---
  const page = parseInt(searchParams.get('page')) || 1;
  const limit = 50;
  const q = searchParams.get('q') || "";
  const invusenum = searchParams.get('invusenum') || "";
  const description = searchParams.get('description') || "";
  const usage_type = searchParams.get('usage_type') || "ALL"; // ALL, ISSUE, TRANSFER, RETURN
  const fromstore = searchParams.get('fromstore') || "";
  const status = searchParams.get('status') || "";
  const sortBy = searchParams.get('sortBy') || "entereddate";
  const sortDir = searchParams.get('sortDir') || "desc";

  const fetchUsageList = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const effectiveSite = !showAllSites && !searchParams.get('site_id') ? sessionSite : searchParams.get('site_id');

      const res = await axios.get('/api/inventory/usage-history', {
        params: { 
          page, limit, q, invusenum, description, usage_type, fromstore, status,
          site_id: effectiveSite, org_id: sessionOrg, all_sites: showAllSites,
          sortBy, sortDir 
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setUsageRecords(res.data.data || []);
      setTotalCount(res.data.total || 0);
    } catch (err) {
      console.error("Error fetching usage history:", err);
      toast.error(t('common.load_error', "Failed to load usage history"));
    } finally {
      setLoading(false);
    }
  }, [page, q, invusenum, description, usage_type, fromstore, status, searchParams, sortBy, sortDir, showAllSites, sessionSite, sessionOrg, t]);

  useEffect(() => {
    const timer = setTimeout(() => fetchUsageList(), 100);
    return () => clearTimeout(timer);
  }, [fetchUsageList]);

  // --- Filter Handlers ---
  const updateFilters = (newFilters) => {
    const params = Object.fromEntries(searchParams.entries());
    const updated = { ...params, ...newFilters };
    Object.keys(updated).forEach(key => !updated[key] && delete updated[key]);
    setSearchParams(updated);
  };

  const handleSort = (column) => {
    const direction = sortBy === column && sortDir === 'asc' ? 'desc' : 'asc';
    updateFilters({ sortBy: column, sortDir: direction, page: 1 });
  };

  const handleRowClick = (id) => {
    const activeFilters = Object.fromEntries(searchParams.entries());
    const effectiveSite = !showAllSites && !activeFilters.site_id ? sessionSite : activeFilters.site_id;
    let navParams = { ...activeFilters, org_id: sessionOrg };
    if (effectiveSite) navParams.site_id = effectiveSite; else delete navParams.site_id;
    if (showAllSites) navParams.all_sites = true;
    
    const queryParams = new URLSearchParams(navParams).toString();
    navigate(`/inventoryusage/${id}${queryParams ? '?' + queryParams : ''}`);
  };

  const handleClearFilters = () => {
    setSearchParams({});
    setShowAllSites(false);
  };

  // --- Sidebar Events ---
  useEffect(() => {
      const openAllSites = () => { setShowAllSites(true); updateFilters({ page: 1, all_sites: true }); };
      const viewMySite = () => { setShowAllSites(false); updateFilters({ page: 1, all_sites: false }); };
      
      const exportData = () => {
          const activeFilters = Object.fromEntries(searchParams.entries());
          const effectiveSite = !showAllSites && !activeFilters.site_id ? sessionSite : activeFilters.site_id;
          let exportParams = { ...activeFilters, org_id: sessionOrg };
          if (effectiveSite) exportParams.site_id = effectiveSite; else delete exportParams.site_id;

          const columnsMap = {
              "invusenum": t('invusage.fields.usagenum', 'Usage ID'),
              "description": t('common.description', 'Description'),
              "usage_type": t('invusage.fields.type', 'Type'),
              "fromstore": t('invusage.fields.fromstore', 'From Store'),
              "status": t('common.status', 'Status'),
              "entereddate": t('common.date', 'Date')
          };
          exportToExcel('invusage', exportParams, columnsMap, t);
      };

      window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
      window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
      window.addEventListener('GLOBAL_EXPORT', exportData);
      
      return () => {
          window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
          window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
          window.removeEventListener('GLOBAL_EXPORT', exportData);
      };
  }, [searchParams, showAllSites, sessionSite, sessionOrg, t]);

  const totalPages = Math.ceil(totalCount / limit) || 1;

  // --- UI Helpers ---
  const getStatusBadge = (recStatus) => {
    const styles = {
      'COMPLETED': 'bg-status-success-bg text-status-success border-status-success/30',
      'DRAFT': 'bg-status-warning-bg text-status-warning border-status-warning/30',
      'CANCELLED': 'bg-status-danger-bg text-status-danger border-status-danger/30',
      'INPRG': 'bg-status-info-bg text-status-info border-status-info/30'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase border ${styles[recStatus] || styles['DRAFT']}`}>
        {recStatus || 'DRAFT'}
      </span>
    );
  };

  return (
    <div className={`p-8 bg-surface-app min-h-screen text-left select-none animate-in fade-in duration-500 ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* --- Header Section --- */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <div className="bg-primary-dark p-3 rounded-xl shadow-lg text-white">
            <LayoutGrid size={24} />
          </div>
          <div className="flex flex-col">
            <h1 className="text-2xl font-black text-ink tracking-tighter uppercase italic flex items-center gap-2">
              {t('invusage.list.title', 'Inventory Usage History')}
              <span className={`text-[10px] px-2 py-0.5 rounded-full border not-italic font-bold ${showAllSites ? 'bg-status-danger-bg text-status-danger border-status-danger-bg' : 'bg-status-info-bg text-status-info border-status-info-bg'}`}>
                {showAllSites ? t('common.global_view', 'GLOBAL VIEW') : `${t('common.site', 'SITE')}: ${sessionSite}`}
              </span>
            </h1>
            <p className="text-[10px] text-ink-secondary font-bold uppercase tracking-[0.2em]">
              {t('common.org', 'Org')}: <span className="text-primary">{sessionOrg}</span>
            </p>
          </div>
        </div>

        {canCreate && (
          <button
            onClick={() => navigate('/inventoryusage/new')}
            className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-sm text-[11px] font-black uppercase shadow-xl flex items-center gap-3 transition-all active:scale-95"
          >
            <Plus size={16} /> {t('invusage.list.new', 'Create New Transaction')}
          </button>
        )}
      </div>

      {/* --- Filters & Search Bar --- */}
      <div className="bg-surface-card p-4 border border-line rounded-sm shadow-sm flex flex-wrap gap-4 items-center mb-6 z-20">
        <div className={`relative flex-1 min-w-[300px] flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Search className={`absolute text-ink-secondary ${isRTL ? 'right-4' : 'left-4'}`} size={18} />
          <input
            type="text"
            placeholder={t('common.search')}
            className={`w-full bg-surface-panel border-none rounded-sm py-3 px-4 text-[11px] font-bold outline-none focus:ring-2 focus:ring-primary/20 transition-all uppercase ${isRTL ? 'pr-12' : 'pl-12'}`}
            value={q}
            onChange={(e) => updateFilters({ q: e.target.value, page: 1 })}
          />
        </div>

        <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {['ALL', 'ISSUE', 'TRANSFER', 'RETURN'].map(type => (
            <button
              key={type}
              onClick={() => updateFilters({ usage_type: type, page: 1 })}
              className={`px-6 py-2 rounded-sm text-[9px] font-black uppercase tracking-widest transition-all ${
                usage_type === type ? 'bg-primary-dark text-white shadow-md' : 'bg-surface-panel text-ink-secondary hover:bg-line'
              }`}
            >
              {t(`invusage.types.${type.toLowerCase()}`, type)}
            </button>
          ))}
        </div>

        <div className="flex gap-2 items-center border-l pl-4 border-line">
          <button onClick={handleClearFilters} className="flex items-center gap-2 px-3 py-2 text-ink-secondary hover:text-status-danger font-bold transition-colors text-[11px] uppercase">
            <X size={14}/> {t('common.clear_filters', 'Clear')}
          </button>
          <button onClick={() => fetchUsageList()} className="p-2.5 bg-surface-panel text-primary hover:bg-primary-soft transition-all rounded-sm shadow-sm active:rotate-180 duration-500" title="Refresh">
            <RefreshCw size={16} className={loading ? "animate-spin text-status-warning" : ""} />
          </button>
        </div>
      </div>

      {/* --- Data Table --- */}
      <div className="bg-surface-card border border-line shadow-2xl rounded-sm overflow-hidden min-h-[450px]">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-surface-panel text-ink-secondary text-[10px] tracking-widest sticky top-0 z-10">
              <SortableHeader label={t('invusage.fields.usagenum', 'Usage ID')} sortKey="invusenum" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
              <SortableHeader label={t('common.description', 'Description')} sortKey="description" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
              <SortableHeader label={t('invusage.fields.type', 'Type')} sortKey="usage_type" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
              <th className="p-4 border-x border-line text-center font-black uppercase">{t('invusage.fields.lines', 'Lines')}</th>
              <SortableHeader label={t('common.status', 'Status')} sortKey="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
              <SortableHeader label={t('common.date', 'Date')} sortKey="entereddate" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
            </tr>
          </thead>
          <tbody className="divide-y divide-line-subtle uppercase">
            {loading ? (
              <tr>
                <td colSpan="6" className="p-20 text-center">
                    <div className="flex flex-col items-center gap-3">
                        <Loader2 className="animate-spin text-primary-dark" size={40}/>
                        <span className="text-[10px] font-black text-ink-secondary animate-pulse">{t('common.loading', 'Fetching records...')}</span>
                    </div>
                </td>
              </tr>
            ) : usageRecords.length === 0 ? (
                <tr>
                    <td colSpan="6" className="p-20 text-center">
                        <div className="flex flex-col items-center gap-4 opacity-20">
                            <FileText size={60} />
                            <span className="text-[11px] font-black uppercase tracking-[0.5em]">{t('common.no_records', 'No Records Found')}</span>
                        </div>
                    </td>
                </tr>
            ) : (
              usageRecords.map((record) => (
                <motion.tr 
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  key={record.invusenum} 
                  onClick={() => handleRowClick(record.invusenum)}
                  className={`hover:bg-primary-soft/40 transition-all group cursor-pointer border-b border-line-subtle ${isRTL ? 'text-right' : 'text-left'}`}
                >
                  <td className="p-4 border-x border-line-subtle">
                    <span className="text-[10px] font-black text-primary-dark bg-primary-soft border border-primary/20 px-3 py-1 rounded-sm shadow-sm italic group-hover:underline">
                      {record.invusenum}
                    </span>
                  </td>

                  <td className="p-4 border-x border-line-subtle">
                    <div className="flex flex-col">
                      <span className="text-[11px] font-black text-ink uppercase tracking-tighter truncate max-w-[250px]">
                        {record.description || '---'}
                      </span>
                      <div className={`flex items-center gap-1 mt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span className="text-[9px] text-primary font-bold uppercase italic">{record.fromstore || '---'}</span>
                        {record.usage_type === 'TRANSFER' && record.tostore && (
                            <>
                                <ArrowRightLeft size={10} className="text-ink-secondary mx-1" />
                                <span className="text-[9px] text-purple-600 font-bold uppercase italic">{record.tostore}</span>
                            </>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="p-4 border-x border-line-subtle text-center">
                      <span className={`inline-flex items-center gap-1 text-[9px] font-black bg-surface-panel px-2.5 py-1 rounded-sm uppercase border border-line shadow-sm ${record.usage_type === 'ISSUE' ? 'text-status-warning' : record.usage_type === 'RETURN' ? 'text-status-success' : 'text-purple-600'}`}>
                          <RefreshCw size={10} className={record.usage_type === 'TRANSFER' ? 'rotate-90' : ''}/>
                          {t(`invusage.types.${record.usage_type?.toLowerCase()}`, record.usage_type)}
                      </span>
                  </td>

                  <td className="p-4 border-x border-line-subtle text-center">
                    <div className="flex flex-col items-center">
                      <span className="text-[11px] font-black text-ink bg-surface-panel w-8 h-8 rounded-full flex items-center justify-center border border-line">
                        {record.usage_lines ? record.usage_lines.length : 0}
                      </span>
                    </div>
                  </td>

                  <td className="p-4 border-x border-line-subtle text-center">
                    {getStatusBadge(record.status)}
                  </td>

                  <td className="p-4 border-x border-line-subtle text-center">
                    <div className="flex flex-col items-center">
                      <span className="text-[10px] font-black text-ink tracking-tighter bg-surface-panel px-2 py-0.5 rounded font-mono">
                        {record.entereddate ? new Date(record.entereddate).toLocaleDateString() : '---'}
                      </span>
                      <span className={`text-[8px] text-ink-secondary font-bold uppercase tracking-widest flex items-center gap-1 mt-1.5 italic ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Clock size={10}/> {t('invusage.fields.enterby', 'By')}: <span className="text-primary">{record.enterby || '---'}</span>
                      </span>
                    </div>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* --- Pagination Footer --- */}
      <div className={`bg-surface-card border-t border-line p-2 px-8 flex justify-between items-center shadow-sm z-30 font-black text-[10px] text-ink-secondary rounded-b-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Box size={14} className="text-primary"/>
                <span>{t('common.showing', 'Showing')}: <span className="text-primary">{(page-1)*limit+1}-{Math.min(page*limit, totalCount)}</span></span>
            </div>
            <div className={`flex items-center gap-2 border-line ${isRTL ? 'border-r pr-6 flex-row-reverse' : 'border-l pl-6'}`}>
                <Filter size={14} className="text-status-warning"/>
                <span>{t('common.total', 'Total')}: <span className="text-status-warning">{totalCount}</span></span>
            </div>
        </div>

        <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-1 bg-surface-panel rounded px-2 py-1 border border-line ${isRTL ? 'flex-row-reverse' : ''}`}>
                <button disabled={page === 1} onClick={() => updateFilters({page: 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronsLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button disabled={page === 1} onClick={() => updateFilters({page: page - 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <div className="px-4 text-[11px] font-mono border-x border-line mx-1">
                    <span className="text-primary text-sm">{page}</span> / {totalPages}
                </div>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: page + 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: totalPages})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronsRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
        </div>
      </div>
    </div>
  );
};

// --- Helper Component ---
const SortableHeader = ({ label, sortKey, sortBy, sortDir, onSort, align = "left", isRTL }) => (
  <th className={`p-4 border-x border-line cursor-pointer hover:bg-primary-soft transition-colors text-${align === 'center' ? 'center' : (isRTL ? 'right' : 'left')}`} onClick={() => onSort(sortKey)}>
      <div className={`flex items-center gap-2 ${align === 'center' ? 'justify-center' : ''} ${isRTL ? 'flex-row-reverse' : ''}`}>
          {label}
          <ArrowUpDown size={12} className={sortBy === sortKey ? 'text-primary' : 'text-ink-secondary'} />
      </div>
  </th>
);

export default InventoryUsageList;